import React, { useEffect, useState } from "react";
import { Box, Button, Heading, TextInput, Spinner, Grid, Text, Menu } from "grommet";
import axios from "axios";
import { take } from "lodash";
import { useUser, Attendee } from "../utils/AccountStore";
import { useNavigate } from "react-router-dom";
import { CaretRightFill, Down, Home, Up } from "grommet-icons";

export interface Exhibitor {
    id: string;
    name: string;
    logo: string;
    description: string;
    isTreasure: boolean;
    location: string;
    website: string;
}

export const GetExhibitors = async () => {
    return axios.get(`${Server}/api/exhibitors`);
};

export const ExhibitorRow = ({ exhibitor, onClick }: { exhibitor: Exhibitor; onClick: () => void }) => (
    <Box direction="row" gap="medium" align="center" pad="medium" background="brand" round="small" justify="between" onClick={onClick} fill="horizontal">
        <Box direction="row" gap="medium" align="center">
            <img
                src={exhibitor.logo}
                alt={`${exhibitor.name} logo`}
                style={{
                    width: 100,
                    height: 100,
                    objectFit: "contain",
                    borderRadius: "10px",
                }}
            />
            <Box>
                <Heading level="4" margin="none" color={"white"}>
                    {exhibitor.name}
                </Heading>
                {exhibitor.location && (
                    <Text size="small" color={"white"}>
                        {exhibitor.location}
                    </Text>
                )}
            </Box>
        </Box>
        <Text size="large">
            <CaretRightFill color={"white"} />
        </Text>
    </Box>
);

const Exhibitors = () => {
    const [exhibitors, setExhibitors] = useState<Exhibitor[]>([]);
    const [filteredExhibitors, setFilteredExhibitors] = useState<Exhibitor[]>([]);
    const [sort, setSort] = useState("name");
    const [sortOrder, setSortOrder] = useState("ascending");
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);

    const { user, getUser } = useUser();

    const navigate = useNavigate();

    useEffect(() => {
        GetExhibitors()
            .then((res) => {
                if (res.status === 200) {
                    setExhibitors(res.data);
                    setLoading(false);
                } else {
                    alert("Error getting exhibitors");
                    navigate("/home");
                }
            })
            .catch(() => {
                alert("Error getting exhibitors");
                navigate("/home");
            });

        getUser();
    }, []);

    useEffect(() => {
        setFilteredExhibitors(exhibitors.filter((e) => e.name.toLowerCase().includes(filter.toLowerCase())));
        setPageSize(10);
    }, [exhibitors, filter]);

    useEffect(() => {
        handleSort();
        setPageSize(10);
    }, [sort, sortOrder]);

    const handleSort = () => {
        const sorted = [...filteredExhibitors].sort((a, b) => {
            const keyA = a[sort].toLowerCase();
            const keyB = b[sort].toLowerCase();
            return sortOrder === "ascending" ? keyA.localeCompare(keyB) : keyB.localeCompare(keyA);
        });
        setFilteredExhibitors(sorted);
    };

    const toggleSort = (field: "name" | "location") => {
        if (sort !== field) {
            setSort(field);
            setSortOrder("ascending");
        } else {
            setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
        }
    };

    return (
        <Box pad="medium" align="center" fill gap={"small"}>
            <Grid columns={["1fr", "auto", "1fr"]} width="100%" align="center">
                <Home color="brand" onClick={() => navigate("/home")} />
                <Heading level="2" color="brand">
                    Zones
                </Heading>
                <Box direction="row" gap="small" align="center" justify="end">
                    <Menu
                        label={"Sort"}
                        items={[
                            { label: "By Name", onClick: () => toggleSort("name") },
                            { label: "By Location", onClick: () => toggleSort("location") },
                        ]}
                    />
                </Box>
            </Grid>

            {/* <TextInput placeholder="Search exhibitors..." value={filter} onChange={(event) => setFilter(event.target.value)} /> */}
            {/* <Button label="View Floor Plans" onClick={() => navigate("/floorPlans")} color="brand" primary /> */}

            {loading ? (
                <Spinner size="medium" margin={{ top: "large" }} />
            ) : (
                <Box margin={{ top: "medium" }} width="100%">
                    <Grid columns="medium" gap="medium">
                        {take(filteredExhibitors, pageSize).map((exhibitor) => (
                            <ExhibitorRow key={exhibitor.id} exhibitor={exhibitor} onClick={() => navigate("/exhibitorDetails/" + exhibitor.id)} />
                        ))}
                    </Grid>

                    {filteredExhibitors.length > pageSize && <Button label="Load More" margin={{ top: "medium" }} onClick={() => setPageSize(pageSize + 10)} />}
                </Box>
            )}
        </Box>
    );
};

export default Exhibitors;
